/**
 * Utility functions to sync Gantt chart tasks to the database
 */

import type { GanttTask } from '../types/gantt';
import * as tasksAPI from '../services/tasks';

/**
 * Convert GanttTask to database task format
 */
function ganttTaskToDatabaseTask(
	ganttTask: GanttTask,
	projectId: string | number
): tasksAPI.CreateTaskPayload {
	const projectIdNum = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
	
	return {
		project_id: isNaN(projectIdNum) ? undefined : projectIdNum,
		title: ganttTask.name,
		description: ganttTask.description,
		type: 'task', // Gantt tasks are always 'task' type
		status: ganttTask.status || 'not_started',
		start_date: ganttTask.startDate ? ganttTask.startDate.split('T')[0] : undefined,
		end_date: ganttTask.endDate ? ganttTask.endDate.split('T')[0] : undefined,
		due_date: ganttTask.endDate ? ganttTask.endDate.split('T')[0] : undefined, // Use end_date as due_date
		duration: ganttTask.duration,
		progress: ganttTask.progress || 0,
		dependencies: ganttTask.dependencies || [],
		is_critical: ganttTask.isCritical || false,
		gardener_ids: ganttTask.gardenerIds 
			? ganttTask.gardenerIds.map(id => {
				const numId = typeof id === 'string' ? parseInt(id, 10) : id;
				return isNaN(numId) ? undefined : numId;
			}).filter((id): id is number => id !== undefined)
			: ganttTask.gardenerId 
				? (() => {
					const numId = typeof ganttTask.gardenerId === 'string' 
						? parseInt(ganttTask.gardenerId, 10) 
						: ganttTask.gardenerId;
					return isNaN(numId) ? [] : [numId];
				})()
				: undefined,
		machine_ids: ganttTask.machineIds || undefined,
		notes: (ganttTask as any).notes || undefined
	};
}

/**
 * Find existing task in database by matching title, project_id, and start_date
 * This helps identify tasks that were created but lost their database ID
 */
async function findExistingTaskInDatabase(
	taskName: string,
	projectId: number,
	startDate: string
): Promise<number | null> {
	try {
		// Get all tasks for this project
		const projectTasks = await tasksAPI.getTasks({ project_id: projectId });
		
		// Find task with matching name and start date
		const startDateStr = startDate.split('T')[0]; // Just the date part
		const matchingTask = projectTasks.find(task => 
			task.title === taskName && 
			task.start_date === startDateStr
		);
		
		return matchingTask ? matchingTask.id : null;
	} catch (err) {
		console.error('Failed to search for existing task:', err);
		return null;
	}
}

/**
 * Sync a single Gantt task to the database
 * If task has a database ID (stored in task.id as "db-{id}"), update it
 * Otherwise, try to find existing task or create a new one
 */
export async function syncGanttTaskToDatabase(
	ganttTask: GanttTask,
	projectId: string | number
): Promise<number | null> {
	try {
		const projectIdNum = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
		if (isNaN(projectIdNum)) {
			console.error('Invalid project ID:', projectId);
			return null;
		}

		// Check if task already has a database ID
		// Format: "db-{id}" or just the numeric ID
		let existingTaskId: number | null = null;
		if (ganttTask.id.startsWith('db-')) {
			const dbId = parseInt(ganttTask.id.replace('db-', ''), 10);
			if (!isNaN(dbId)) {
				existingTaskId = dbId;
			}
		} else {
			// Check if it's a numeric ID (from database)
			const numId = parseInt(ganttTask.id, 10);
			if (!isNaN(numId) && numId > 1000) { // Database IDs are typically > 1000
				existingTaskId = numId;
			} else {
				// Try to find existing task in database by matching name and start date
				existingTaskId = await findExistingTaskInDatabase(
					ganttTask.name,
					projectIdNum,
					ganttTask.startDate
				);
			}
		}

		const taskData = ganttTaskToDatabaseTask(ganttTask, projectIdNum);

		if (existingTaskId) {
			// Update existing task
			try {
				await tasksAPI.updateTask(existingTaskId, taskData);
				return existingTaskId;
			} catch (err) {
				console.error(`Failed to update task ${existingTaskId}:`, err);
				// If update fails (task might have been deleted), create new one
				existingTaskId = null;
			}
		}

		if (!existingTaskId) {
			// Create new task
			const createdTask = await tasksAPI.createTask(taskData);
			return createdTask.id;
		}

		return existingTaskId;
	} catch (err) {
		console.error('Failed to sync task to database:', err);
		return null;
	}
}

/**
 * Sync all Gantt tasks from a project to the database
 */
export async function syncProjectGanttTasksToDatabase(
	ganttTasks: GanttTask[],
	projectId: string | number
): Promise<void> {
	try {
		const syncPromises = ganttTasks.map(task => 
			syncGanttTaskToDatabase(task, projectId)
		);
		
		await Promise.all(syncPromises);
	} catch (err) {
		console.error('Failed to sync tasks to database:', err);
	}
}

/**
 * Update Gantt task ID to include database ID
 * This allows us to track which tasks are in the database
 */
export function updateGanttTaskWithDatabaseId(
	ganttTask: GanttTask,
	databaseId: number
): GanttTask {
	return {
		...ganttTask,
		id: `db-${databaseId}` // Store as "db-{id}" to distinguish from generated IDs
	};
}

